import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  InputAdornment,
  Menu,
  MenuItem,
  Divider,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ListIcon from '@material-ui/icons/List';
import EmailIcon from '@material-ui/icons/Email';
import SearchIcon from '@material-ui/icons/Search';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useHistory } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    minHeight: '100vh',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(2),
    },
  },
  headerButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
      '& .MuiButton-root, & label': {
        width: '100%',
      },
      '& .MuiButton-root': {
        justifyContent: 'center',
      },
    },
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  searchField: {
    backgroundColor: 'white',
    marginBottom: theme.spacing(4),
  },
  listCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  listName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    fontSize: '1.1rem',
  },
  listDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    minHeight: '2em',
    fontSize: '0.875rem',
  },
  recipientCount: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  emptyIcon: {
    fontSize: '3rem',
    color: theme.palette.grey[400],
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    maxHeight: 500,
  },
  bulkInput: {
    marginTop: theme.spacing(2),
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  cardActions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end',
  },
}));

const ManageRecipientLists = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [recipientSearchTerm, setRecipientSearchTerm] = useState('');
  
  // List dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, list: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, listId: null, listName: '' });
  
  // Recipients dialog
  const [recipientsDialog, setRecipientsDialog] = useState({ open: false, list: null, recipients: [] });
  const [addRecipientDialog, setAddRecipientDialog] = useState({ open: false, listId: null });
  
  // Recipient management dialogs
  const [editRecipientDialog, setEditRecipientDialog] = useState({ open: false, recipient: null, listId: null });
  const [deleteRecipientDialog, setDeleteRecipientDialog] = useState({ open: false, recipientId: null, email: '' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  
  // Form states
  const [listForm, setListForm] = useState({ name: '', description: '' });
  const [recipientForm, setRecipientForm] = useState({ email: '', firstName: '', lastName: '' });
  const [bulkEmails, setBulkEmails] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadLists();
  }, []);

  const loadLists = () => {
    setIsLoading(true);
    axios.get(`${API_URL}email-campaigns/lists`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      setLists(response.data);
      setIsLoading(false);
    })
    .catch(error => {
      console.error('Error loading lists:', error);
      showSnackbar('Failed to load recipient lists', 'error');
      setIsLoading(false);
    });
  };

  const handleCreateList = () => {
    if (!listForm.name.trim()) {
      showSnackbar('List name is required', 'error');
      return;
    }

    axios.post(`${API_URL}email-campaigns/lists`, listForm, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      showSnackbar('List created successfully', 'success');
      setCreateDialog(false);
      setListForm({ name: '', description: '' });
      loadLists();
    })
    .catch(error => {
      console.error('Error creating list:', error);
      showSnackbar('Failed to create list', 'error');
    });
  };

  const handleUpdateList = () => {
    if (!listForm.name.trim()) {
      showSnackbar('List name is required', 'error');
      return;
    }

    axios.put(`${API_URL}email-campaigns/lists/${editDialog.list.id}`, listForm, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      showSnackbar('List updated successfully', 'success');
      setEditDialog({ open: false, list: null });
      setListForm({ name: '', description: '' });
      loadLists();
    })
    .catch(error => {
      console.error('Error updating list:', error);
      showSnackbar('Failed to update list', 'error');
    });
  };

  const handleDeleteList = () => {
    axios.delete(`${API_URL}email-campaigns/lists/${deleteDialog.listId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      showSnackbar('List deleted successfully', 'success');
      setDeleteDialog({ open: false, listId: null, listName: '' });
      loadLists();
    })
    .catch(error => {
      console.error('Error deleting list:', error);
      showSnackbar('Failed to delete list', 'error');
    });
  };

  const loadRecipients = (list) => {
    axios.get(`${API_URL}email-campaigns/lists/${list.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      setRecipientsDialog({ 
        open: true, 
        list: list, 
        recipients: response.data.recipients || []
      });
    })
    .catch(error => {
      console.error('Error loading recipients:', error);
      showSnackbar('Failed to load recipients', 'error');
    });
  };

  const handleAddSingleRecipient = () => {
    if (!recipientForm.email.trim()) {
      showSnackbar('Email is required', 'error');
      return;
    }

    const recipients = [{
      email: recipientForm.email,
      firstName: recipientForm.firstName,
      lastName: recipientForm.lastName
    }];

    axios.post(`${API_URL}email-campaigns/lists/${addRecipientDialog.listId}/recipients`, 
      { recipients },
      { headers: { Authorization: `Bearer ${token}` }}
    )
    .then(response => {
      showSnackbar('Recipient added successfully', 'success');
      setRecipientForm({ email: '', firstName: '', lastName: '' });
      setAddRecipientDialog({ open: false, listId: null });
      
      // Reload recipients if the dialog is open
      const currentList = lists.find(l => l.id === addRecipientDialog.listId);
      if (currentList && recipientsDialog.open) {
        loadRecipients(currentList);
      }
      loadLists();
    })
    .catch(error => {
      console.error('Error adding recipient:', error);
      showSnackbar('Failed to add recipient', 'error');
    });
  };

  const handleBulkAddRecipients = () => {
    if (!bulkEmails.trim()) {
      showSnackbar('Please enter at least one email', 'error');
      return;
    }

    // Parse emails from textarea (comma, semicolon, or newline separated)
    const emailList = bulkEmails
      .split(/[,;\n]/)
      .map(e => e.trim())
      .filter(e => e.length > 0)
      .map(email => ({ email, firstName: null, lastName: null }));

    if (emailList.length === 0) {
      showSnackbar('No valid emails found', 'error');
      return;
    }

    axios.post(`${API_URL}email-campaigns/lists/${addRecipientDialog.listId}/recipients`, 
      { recipients: emailList },
      { headers: { Authorization: `Bearer ${token}` }}
    )
    .then(response => {
      showSnackbar(`${emailList.length} recipient(s) added successfully`, 'success');
      setBulkEmails('');
      setAddRecipientDialog({ open: false, listId: null });
      
      // Reload recipients if the dialog is open
      const currentList = lists.find(l => l.id === addRecipientDialog.listId);
      if (currentList && recipientsDialog.open) {
        loadRecipients(currentList);
      }
      loadLists();
    })
    .catch(error => {
      console.error('Error adding recipients:', error);
      showSnackbar('Failed to add recipients', 'error');
    });
  };

  const handleRecipientMenuOpen = (event, recipient) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecipient(recipient);
  };

  const handleRecipientMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecipient(null);
  };

  const openEditRecipient = () => {
    setEditRecipientDialog({
      open: true,
      recipient: selectedRecipient,
      listId: recipientsDialog.list.id
    });
    setRecipientForm({
      email: selectedRecipient.email,
      firstName: selectedRecipient.first_name || '',
      lastName: selectedRecipient.last_name || ''
    });
    handleRecipientMenuClose();
  };

  const openDeleteRecipient = () => {
    setDeleteRecipientDialog({
      open: true,
      recipientId: selectedRecipient.id,
      email: selectedRecipient.email
    });
    handleRecipientMenuClose();
  };

  const handleUpdateRecipient = () => {
    if (!recipientForm.email.trim()) {
      showSnackbar('Email is required', 'error');
      return;
    }

    axios.put(
      `${API_URL}email-campaigns/lists/${editRecipientDialog.listId}/recipients/${editRecipientDialog.recipient.id}`,
      {
        email: recipientForm.email,
        firstName: recipientForm.firstName || null,
        lastName: recipientForm.lastName || null
      },
      { headers: { Authorization: `Bearer ${token}` }}
    )
    .then(response => {
      showSnackbar('Recipient updated successfully', 'success');
      setEditRecipientDialog({ open: false, recipient: null, listId: null });
      setRecipientForm({ email: '', firstName: '', lastName: '' });
      
      // Reload recipients
      loadRecipients(recipientsDialog.list);
      loadLists();
    })
    .catch(error => {
      console.error('Error updating recipient:', error);
      showSnackbar('Failed to update recipient', 'error');
    });
  };

  const handleDeleteRecipient = () => {
    axios.delete(
      `${API_URL}email-campaigns/lists/${recipientsDialog.list.id}/recipients/${deleteRecipientDialog.recipientId}`,
      { headers: { Authorization: `Bearer ${token}` }}
    )
    .then(response => {
      showSnackbar('Recipient removed successfully', 'success');
      setDeleteRecipientDialog({ open: false, recipientId: null, email: '' });
      
      // Reload recipients
      loadRecipients(recipientsDialog.list);
      loadLists();
    })
    .catch(error => {
      console.error('Error deleting recipient:', error);
      showSnackbar('Failed to remove recipient', 'error');
    });
  };

  // CSV Template Download
  const handleDownloadTemplate = () => {
    const csvContent = 'email,first_name,last_name\njohn@example.com,John,Doe\njane@example.com,Jane,Smith\nbob@example.com,Bob,Johnson';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'recipient-list-template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
    showSnackbar('Template downloaded successfully', 'success');
  };

  // CSV Upload and Parse
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          showSnackbar('CSV file is empty', 'error');
          return;
        }

        // Check for header row
        const firstLine = lines[0].toLowerCase();
        const hasHeader = firstLine.includes('email') || firstLine.includes('first') || firstLine.includes('last');
        const startIndex = hasHeader ? 1 : 0;

        // Parse recipients
        const parsedRecipients = [];
        for (let i = startIndex; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          // Split by comma (handle potential quotes)
          const parts = line.split(',').map(part => part.trim().replace(/^["']|["']$/g, ''));
          
          const email = parts[0] || '';
          const firstName = parts[1] || '';
          const lastName = parts[2] || '';

          // Validate email
          if (email && email.includes('@')) {
            parsedRecipients.push({ email, firstName, lastName });
          }
        }

        if (parsedRecipients.length === 0) {
          showSnackbar('No valid email addresses found in CSV', 'error');
          return;
        }

        // Show dialog to select destination list
        handleCsvImportDialog(parsedRecipients);
        
      } catch (error) {
        console.error('Error parsing CSV:', error);
        showSnackbar('Failed to parse CSV file', 'error');
      }
    };

    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  // CSV Import Dialog - let user choose list or create new one
  const handleCsvImportDialog = (recipients) => {
    const listName = prompt(`Import ${recipients.length} recipient(s) into which list?\n\nEnter list name (or leave empty to create new list):`);
    
    if (listName === null) return; // User cancelled

    // Check if list exists
    const existingList = lists.find(list => list.name.toLowerCase() === listName.toLowerCase());
    
    if (existingList) {
      // Add to existing list
      importRecipientsToList(existingList.id, recipients);
    } else if (listName.trim()) {
      // Create new list and add recipients
      createListAndImportRecipients(listName.trim(), recipients);
    } else {
      showSnackbar('List name is required', 'error');
    }
  };

  // Import recipients to existing list
  const importRecipientsToList = (listId, recipients) => {
    const recipientData = recipients.map(r => ({
      email: r.email,
      first_name: r.firstName,
      last_name: r.lastName
    }));

    // Add recipients one by one (or use bulk endpoint if available)
    let successCount = 0;
    let errorCount = 0;

    const addPromises = recipientData.map(recipient => 
      axios.post(`${API_URL}email-campaigns/lists/${listId}/recipients`, recipient, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(() => successCount++)
      .catch(() => errorCount++)
    );

    Promise.all(addPromises).then(() => {
      if (successCount > 0) {
        showSnackbar(`Successfully imported ${successCount} recipient(s)`, 'success');
        loadLists();
      }
      if (errorCount > 0) {
        showSnackbar(`${errorCount} recipient(s) failed to import (may be duplicates)`, 'warning');
      }
    });
  };

  // Create new list and import recipients
  const createListAndImportRecipients = (name, recipients) => {
    axios.post(`${API_URL}email-campaigns/lists`, { name, description: 'Imported from CSV' }, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => {
      const newListId = response.data.id;
      importRecipientsToList(newListId, recipients);
    })
    .catch(error => {
      console.error('Error creating list:', error);
      showSnackbar('Failed to create list', 'error');
    });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Filter lists by search term
  const filteredLists = lists.filter(list => 
    list.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (list.description && list.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Filter recipients by search term
  const filteredRecipients = recipientsDialog.recipients.filter(recipient =>
    recipient.email.toLowerCase().includes(recipientSearchTerm.toLowerCase()) ||
    (recipient.first_name && recipient.first_name.toLowerCase().includes(recipientSearchTerm.toLowerCase())) ||
    (recipient.last_name && recipient.last_name.toLowerCase().includes(recipientSearchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" style={{ width: isMobile ? '100%' : 'auto' }}>
          <Tooltip title="Back to Campaigns">
            <IconButton 
              onClick={() => history.push('/email-campaigns')} 
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h4" style={{ marginLeft: 8, fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
            Manage Recipient Lists
          </Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
          <label htmlFor="csv-upload-recipient-lists">
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-upload-recipient-lists"
              onChange={handleCsvUpload}
            />
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
            >
              Import CSV
            </Button>
          </label>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Create New List
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <TextField
        className={classes.searchField}
        fullWidth
        variant="outlined"
        placeholder="Search lists by name or description..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {/* Lists Grid */}
      {filteredLists.length === 0 ? (
        <Paper className={classes.emptyState} elevation={0}>
          <ListIcon className={classes.emptyIcon} />
          <Typography variant="h5" gutterBottom style={{ fontWeight: 500 }}>
            {searchTerm ? 'No lists found' : 'No recipient lists yet'}
          </Typography>
          <Typography color="textSecondary" gutterBottom style={{ marginBottom: 24 }}>
            {searchTerm 
              ? 'Try a different search term' 
              : 'Create your first recipient list to organize email addresses for campaigns'}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialog(true)}
              style={{ marginTop: 16 }}
            >
              Create List
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredLists.map((list) => (
            <Grid item xs={12} sm={6} md={4} key={list.id}>
              <Card className={classes.listCard}>
                <CardContent style={{ padding: '16px', paddingBottom: '8px' }}>
                  <Typography variant="h6" className={classes.listName}>
                    {list.name}
                  </Typography>
                  <Typography className={classes.listDescription}>
                    {list.description || 'No description'}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <EmailIcon style={{ marginRight: 6, color: '#666', fontSize: '1.2rem' }} />
                    <Typography className={classes.recipientCount}>
                      {list.recipient_count || 0}
                    </Typography>
                    <Typography style={{ marginLeft: 6, fontSize: '0.875rem' }} color="textSecondary">
                      recipient{list.recipient_count !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block', fontSize: '0.75rem' }}>
                    Created {new Date(list.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
                <CardActions className={classes.cardActions}>
                  <Tooltip title="View Recipients">
                    <IconButton size="small" onClick={() => loadRecipients(list)}>
                      <EmailIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add Recipients">
                    <IconButton 
                      size="small" 
                      onClick={() => setAddRecipientDialog({ open: true, listId: list.id })}
                    >
                      <PersonAddIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit List">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setListForm({ name: list.name, description: list.description || '' });
                        setEditDialog({ open: true, list });
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete List">
                    <IconButton 
                      size="small" 
                      onClick={() => setDeleteDialog({ open: true, listId: list.id, listName: list.name })}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create List Dialog */}
      <Dialog 
      open={createDialog} 
      onClose={() => setCreateDialog(false)} 
      maxWidth="sm" 
      fullWidth 
      disableEnforceFocus={true}>
        <DialogTitle>Create New Recipient List</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            fullWidth
            variant="outlined"
            value={listForm.name}
            onChange={(e) => setListForm({ ...listForm, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={listForm.description}
            onChange={(e) => setListForm({ ...listForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateList} color="primary" variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit List Dialog */}
      <Dialog 
      open={editDialog.open} 
      onClose={() => setEditDialog({ open: false, list: null })} 
      maxWidth="sm" 
      fullWidth
      disableEnforceFocus={true}>
        <DialogTitle>Edit Recipient List</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            fullWidth
            variant="outlined"
            value={listForm.name}
            onChange={(e) => setListForm({ ...listForm, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={listForm.description}
            onChange={(e) => setListForm({ ...listForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, list: null })}>Cancel</Button>
          <Button onClick={handleUpdateList} color="primary" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
      open={deleteDialog.open} 
      onClose={() => setDeleteDialog({ open: false, listId: null, listName: '' })}
      disableEnforceFocus={true}
      >
        <DialogTitle>Delete Recipient List</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the list <strong>"{deleteDialog.listName}"</strong>? 
            This will also remove all recipients from this list. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, listId: null, listName: '' })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteList} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Recipients Dialog */}
      <Dialog 
        open={recipientsDialog.open} 
        onClose={() => setRecipientsDialog({ open: false, list: null, recipients: [] })}
        maxWidth="md"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle>
          Recipients in "{recipientsDialog.list?.name}"
        </DialogTitle>
        <DialogContent>
          {recipientsDialog.recipients.length === 0 ? (
            <Box textAlign="center" py={3}>
              <PersonAddIcon className={classes.emptyIcon} />
              <Typography variant="h6" gutterBottom style={{ fontWeight: 500 }}>
                No recipients in this list yet
              </Typography>
              <Button
                startIcon={<PersonAddIcon />}
                color="primary"
                variant="contained"
                onClick={() => {
                  setAddRecipientDialog({ open: true, listId: recipientsDialog.list.id });
                  setRecipientsDialog({ open: false, list: null, recipients: [] });
                }}
                style={{ marginTop: 16 }}
              >
                Add Recipients
              </Button>
            </Box>
          ) : (
            <>
              {/* Recipient Search */}
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search recipients by email or name..."
                value={recipientSearchTerm}
                onChange={(e) => setRecipientSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                style={{ marginBottom: 16 }}
              />
              
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>First Name</TableCell>
                      <TableCell>Last Name</TableCell>
                      <TableCell>Added</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRecipients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="textSecondary">No recipients found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRecipients.map((recipient) => (
                        <TableRow key={recipient.id} hover>
                          <TableCell>{recipient.email}</TableCell>
                          <TableCell>{recipient.first_name || '-'}</TableCell>
                          <TableCell>{recipient.last_name || '-'}</TableCell>
                          <TableCell>{new Date(recipient.added_at).toLocaleDateString()}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Manage Recipient">
                              <IconButton 
                                size="small"
                                onClick={(e) => handleRecipientMenuOpen(e, recipient)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setAddRecipientDialog({ open: true, listId: recipientsDialog.list.id });
              setRecipientsDialog({ open: false, list: null, recipients: [] });
            }}
          >
            Add More
          </Button>
          <Button onClick={() => setRecipientsDialog({ open: false, list: null, recipients: [] })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Recipients Dialog */}
      <Dialog 
        open={addRecipientDialog.open} 
        onClose={() => setAddRecipientDialog({ open: false, listId: null })}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle>Add Recipients</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>
            Add Single Recipient
          </Typography>
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={recipientForm.email}
            onChange={(e) => setRecipientForm({ ...recipientForm, email: e.target.value })}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label="First Name (optional)"
                fullWidth
                variant="outlined"
                value={recipientForm.firstName}
                onChange={(e) => setRecipientForm({ ...recipientForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label="Last Name (optional)"
                fullWidth
                variant="outlined"
                value={recipientForm.lastName}
                onChange={(e) => setRecipientForm({ ...recipientForm, lastName: e.target.value })}
              />
            </Grid>
          </Grid>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={handleAddSingleRecipient}
            style={{ marginTop: 8 }}
          >
            Add Single Recipient
          </Button>

          <Typography variant="subtitle2" className={classes.bulkInput} gutterBottom>
            Or Bulk Add Emails
          </Typography>
          <TextField
            margin="dense"
            label="Email addresses (comma, semicolon, or newline separated)"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={bulkEmails}
            onChange={(e) => setBulkEmails(e.target.value)}
            placeholder="email1@example.com, email2@example.com&#10;email3@example.com"
          />
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleBulkAddRecipients}
            style={{ marginTop: 8 }}
          >
            Bulk Add Emails
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRecipientDialog({ open: false, listId: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recipient Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRecipientMenuClose}
      >
        <MenuItem onClick={openEditRecipient}>
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Edit Details
        </MenuItem>
        <MenuItem onClick={openDeleteRecipient}>
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Remove from List
        </MenuItem>
      </Menu>

      {/* Edit Recipient Dialog */}
      <Dialog 
        open={editRecipientDialog.open} 
        onClose={() => {
          setEditRecipientDialog({ open: false, recipient: null, listId: null });
          setRecipientForm({ email: '', firstName: '', lastName: '' });
        }}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle className={classes.dialogTitle}>
          Edit Recipient
        </DialogTitle>
        <Divider />
        <DialogContent style={{ paddingTop: 20 }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            variant="outlined"
            value={recipientForm.email}
            onChange={(e) => setRecipientForm({ ...recipientForm, email: e.target.value })}
            margin="dense"
            required
          />
          <TextField
            fullWidth
            label="First Name"
            variant="outlined"
            value={recipientForm.firstName}
            onChange={(e) => setRecipientForm({ ...recipientForm, firstName: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label="Last Name"
            variant="outlined"
            value={recipientForm.lastName}
            onChange={(e) => setRecipientForm({ ...recipientForm, lastName: e.target.value })}
            margin="dense"
          />
        </DialogContent>
        <Divider />
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => {
              setEditRecipientDialog({ open: false, recipient: null, listId: null });
              setRecipientForm({ email: '', firstName: '', lastName: '' });
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={handleUpdateRecipient}
            startIcon={<EditIcon />}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Recipient Confirmation Dialog */}
      <Dialog
        open={deleteRecipientDialog.open}
        onClose={() => setDeleteRecipientDialog({ open: false, recipientId: null, email: '' })}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle className={classes.dialogTitle}>
          Confirm Removal
        </DialogTitle>
        <Divider />
        <DialogContent style={{ paddingTop: 20 }}>
          <Typography>
            Are you sure you want to remove <strong>{deleteRecipientDialog.email}</strong> from this list?
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={() => setDeleteRecipientDialog({ open: false, recipientId: null, email: '' })}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="secondary"
            onClick={handleDeleteRecipient}
            startIcon={<DeleteIcon />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageRecipientLists;
